import React, { useEffect, useState } from 'react';
import PlaybookHero from '../../../components/playbooks/PlaybookHero';
import { InformationCircleIcon, AcademicCapIcon, PlayCircleIcon, WrenchScrewdriverIcon, ExclamationTriangleIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import VideoEmbed from '../../../components/playbooks/VideoEmbed';
import PlaybookMetrics from '../../../components/playbooks/PlaybookMetrics';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import { getResourceBySlug } from '../../../lib/db';

const PerformanceFrameworkKeyInfo = () => {
  const [resource, setResource] = useState(null);
  const metrics = [
    {
      type: 'difficulty',
      title: 'Difficulty',
      value: 'High - Requires stakeholder alignment and change management'
    },
    {
      type: 'timeline',
      title: 'Timeline',
      value: '4-12 weeks depending on context'
    },
    {
      type: 'size',
      title: 'Team Size',
      value: 'All team sizes'
    },
    {
      type: 'why',
      title: 'Why Use',
      value: 'Build a clearer, more regular & consistent approach to performance measurement'
    },
    {
      type: 'when',
      title: 'When to Use',
      value: 'Building from scratch, or wanting to re-design a cumbersome, heavy performance process that\'s weighing you down'
    }
  ];

  useEffect(() => {
    getResourceBySlug('how-to-build-launch-embed-performance').then(setResource);
  }, []);

  return (
    <div>
      <PlaybookHero
        title={resource?.title || "How to Build, Launch & Embed Your Org's First Performance Framework"}
        subtitle={resource?.description || "A comprehensive guide to building performance frameworks that work"}
        category={resource?.category || "Playbook"}
        bannerUrl={resource?.banner_url}
        readingTime={resource?.timeframe || "15 min read"}
        type={resource?.type}
      />
      <div id="key-info" className="prose prose-invert max-w-none">
        <h2 className="text-2xl font-bold mb-6 flex items-center">
          <InformationCircleIcon className="h-6 w-6 text-primary-light mr-3" />
          Key Info
        </h2>

        {/* What You'll Leave With section */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <h3 className="text-xl font-semibold mb-4 flex items-center">
            <span className="text-primary-light mr-3">—</span>
            What You'll Leave With
          </h3>
          <ul className="space-y-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Clarity on how to build a performance framework that's fit for your stage, team, and goals</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">A working draft of your framework structure, connected to culture and development</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">A simple path to launching, socialising, and embedding performance in everyday rituals</span>
            </li>
          </ul>
        </div>

        {/* Core Principles quote section */}
        <div className="border-l-4 border-primary-light pl-6 py-4 mb-6 bg-gray-900/50 rounded-lg">
          <h4 className="text-lg font-semibold text-white mb-3">Core Principles Behind This Playbook</h4>
          <ul className="space-y-2">
            <li className="text-gray-300 italic">• Performance conversations & awareness should be as regular & continuous as possible</li>
            <li className="text-gray-300 italic">• Lightweight & user-friendly beats cumbersome & over-engineered all day long</li>
            <li className="text-gray-300 italic">• Transparency is a powerful lever. Lean into it where you can.</li>
          </ul>
        </div>

        {/* Design Principles Reference */}
        <p className="text-gray-400 text-sm mb-4">These are taken from our broader design principles at Open Org.</p>
        
        <PlaybookImage 
          src="https://auth.openorg.fyi/storage/v1/object/public/resources/performance/openorg-design-principles.jpg"
          alt="Open Org Design Principles - showing the broader principles that inform our performance framework approach"
          className="mb-8"
        />

        {/* This Guide is for you section */}
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <span className="text-primary-light mr-3">—</span>
          This Guide is for you if any of the following are true:
        </h3>
        <ul className="space-y-4 mb-8">
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">You're moving beyond founder-led or ad hoc feedback</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">You have a career framework but no performance rituals</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">You're seeing bias, confusion or distrust in performance conversations</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-3 font-bold">—</span>
            <span className="text-gray-300">You need something light-touch but structured</span>
          </li>
        </ul>

        {/* Guide Overview */}
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <InformationCircleIcon className="h-6 w-6 text-primary-light mr-3" />
          Guide Overview
        </h3>
        <div className="mb-12">
          <PlaybookMetrics metrics={metrics} />
        </div>

        {/* Watch This section */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <PlayCircleIcon className="h-7 w-7 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">Watch This</h3>
          </div>
          <VideoEmbed 
            src="https://www.loom.com/embed/d25296f87ea64df9814c9760ec5cf7f2" 
            title="Building Performance Frameworks That Work" 
          />
          <div className="text-center text-gray-400 text-sm mt-3">
            Walkthrough: How to build and implement effective performance frameworks
          </div>
        </div>

        {/* What Is This section */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <AcademicCapIcon className="h-6 w-6 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What Is This</h3>
          </div>
          <div className="space-y-4">
            <p className="text-gray-300">
              This comprehensive playbook will guide you through building, launching, and embedding a performance framework that actually works for your organization. You'll create clarity on expectations, build consistent performance conversations, and establish accountability systems that drive results.
            </p>
            <p className="text-gray-300">
              The framework focuses on three core pillars: <strong className="text-white">Results</strong> (what we deliver), <strong className="text-white">Execution</strong> (how effectively we deliver), and <strong className="text-white">Behaviours</strong> (how well we role-model expected behaviours).
            </p>
            <p className="text-gray-300">
              By the end of this playbook, you'll have a working draft of your framework structure, a clear launch plan, and embedded practices that make performance conversations as regular and continuous as possible.
            </p>
          </div>

          <div className="mt-6 p-8 bg-gray-800/50 rounded-lg border border-gray-700">
            <div className="flex flex-col items-center space-y-6">
              <div className="text-center">
                <h5 className="text-white font-semibold text-lg mb-4">Performance Framework Formula</h5>
              </div>
              
              <div className="flex items-start justify-center space-x-4">
                <div className="flex flex-col items-center space-y-3">
                  <div className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold text-base h-10 flex items-center">
                    Results
                  </div>
                  <div className="text-center">
                    <div className="text-gray-400 text-sm font-medium">What we deliver</div>
                    <div className="text-gray-300 text-xs mt-1">relative to expectations & goals</div>
                  </div>
                </div>
                
                <div className="flex items-center h-10 mt-0">
                  <span className="text-primary-light font-bold text-xl">+</span>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <div className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold text-base h-10 flex items-center">
                    Execution
                  </div>
                  <div className="text-center">
                    <div className="text-gray-400 text-sm font-medium">How effectively we deliver</div>
                    <div className="text-gray-300 text-xs mt-1">in context of expected competencies</div>
                  </div>
                </div>
                
                <div className="flex items-center h-10 mt-0">
                  <span className="text-primary-light font-bold text-xl">+</span>
                </div>
                
                <div className="flex flex-col items-center space-y-3">
                  <div className="bg-white text-gray-900 px-4 py-2 rounded-lg font-semibold text-base h-10 flex items-center">
                    Behaviours
                  </div>
                  <div className="text-center">
                    <div className="text-gray-400 text-sm font-medium">How we role-model</div>
                    <div className="text-gray-300 text-xs mt-1">expected behaviours day to day</div>
                  </div>
                </div>
                
                <div className="flex items-center h-10 mt-0">
                  <span className="text-primary-light font-bold text-xl">=</span>
                </div>
                
                <div className="flex items-center">
                  <div className="bg-primary text-white px-6 py-3 rounded-lg font-bold text-base h-12 flex items-center">
                    Performance
                  </div>
                </div>
              </div>
              
              {/* Attribution */}
              <div className="text-center mt-6">
                <img 
                  src="https://auth.openorg.fyi/storage/v1/object/public/web-assets/oo-logo/Default%20Transparent%20PNG.png" 
                  alt="Open Org Logo" 
                  className="h-4 opacity-60 mx-auto"
                />
              </div>
            </div>
          </div>
        </div>

        {/* What You'll Need section */}
        <div className="mb-10 p-6 border border-gray-800 rounded-lg bg-gray-900/50">
          <div className="flex items-center mb-4">
            <WrenchScrewdriverIcon className="h-6 w-6 text-primary-light mr-3" />
            <h3 className="text-xl font-semibold">What You'll Need</h3>
          </div>
          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Senior leadership alignment on performance philosophy</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Basic role clarity and job descriptions</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Clear business goals and values/behaviours</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">Manager buy-in and readiness to facilitate</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-3 font-bold">—</span>
              <span className="text-gray-300">4-12 weeks for design, testing, and launch</span>
            </li>
          </ul>
        </div>

       
        

        {/* Obstacles section using ExpandableCallout */}
        <div className="space-y-6">
          <ExpandableCallout type="warning" title="Common Mistakes We See" defaultOpen={false}>
            <ul className="space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <div>
                  <strong className="text-white">Building something that's too big, too soon:</strong>
                  <p className="text-gray-300 mt-1">Starting with complex multi-layered systems instead of lightweight, user-friendly approaches.</p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <div>
                  <strong className="text-white">Focusing only on results without linking to how work gets done:</strong>
                  <p className="text-gray-300 mt-1">Ignoring execution quality and behavioural expectations in favour of pure outcome metrics.</p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <div>
                  <strong className="text-white">Skipping clarity on why you're assessing performance:</strong>
                  <p className="text-gray-300 mt-1">Launching without clear purpose or connection to business outcomes and employee development.</p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <div>
                  <strong className="text-white">No shared language around what "good" looks like:</strong>
                  <p className="text-gray-300 mt-1">Lack of clear competency frameworks or behavioural expectations leads to inconsistent assessments.</p>
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-3 font-bold">—</span>
                <div>
                  <strong className="text-white">Launching without manager buy-in or team readiness:</strong>
                  <p className="text-gray-300 mt-1">Rolling out before ensuring managers are comfortable and equipped to use the framework effectively.</p>
                </div>
              </li>
            </ul>
          </ExpandableCallout>

          <ExpandableCallout type="warning" title="Where You Might Face Resistance" defaultOpen={false}>
            <div className="space-y-4">
              <p className="text-gray-300">
                This work can feel burdensome to some who may argue you're slowing down on 'building the thing' in favour of focusing on foundational elements. However, these building blocks are vital for any performance framework that works effectively.
              </p>
              <p className="text-gray-300">
                You might encounter stakeholders with different views on what performance assessment should influence - some may prioritize monetary rewards, others growth and development. Getting alignment on this upfront prevents confusion later.
              </p>
              <div className="bg-gray-800/50 p-4 rounded-lg border-l-4 border-primary-light">
                <p className="text-gray-300">
                  <strong className="text-white">Pro tip:</strong> Focus on what your specific components and framework need, and bring conversations back to your core business principles and cultural values when facing resistance.
                </p>
              </div>
            </div>
          </ExpandableCallout>
        </div>
      </div>
    </div>
  );
};

export default PerformanceFrameworkKeyInfo; 